// client/src/pages/print/PrinterDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiFetch } from '../../api/api';
import { getPrinterPrefs, fetchPrinterGroups, savePrinterPrefs } from '../printManager/printManagerApi';
import './printer-detail.css';

const STANDARD_PAPERS = [
  { key: 'LETTER', label: 'Letter (8.5 × 11)', kind: 'Letter' },
  { key: 'A4', label: 'A4', kind: 'A4' },
  { key: 'LEGAL', label: 'Legal', kind: 'Legal' },
  { key: 'A6', label: 'A6', kind: 'A6' },
  { key: 'A5', label: 'A5', kind: 'A5' },
  { key: 'A3', label: 'A3', kind: 'A3' },
  { key: 'TABLOID', label: 'Tabloid', kind: 'Tabloid' },
];

// 🔥 YOUR HARDCODED PHOTO PRINTER MODES
const PHOTO_PRINTER_PAPERS = [
  { key: '6x4', label: '6x4 / 152x100mm', width: 600, height: 400 },
  { key: '5x7', label: '5x7 / 127x178mm', width: 500, height: 700 },
  { key: '6x8', label: '6x8 / 152x203mm', width: 600, height: 800 },

  { key: '6x4-split', label: '6x4-Split (6x2 2 prints)', width: 600, height: 400 },
  { key: '6x8-split', label: '6x8-Split', width: 600, height: 800 },

  { key: '5x7-2up', label: '5x7-2UP / 5x3.5', width: 500, height: 700 },
  { key: '6x8-2up', label: '6x8-2UP / 6x4 (DEFAULT)', width: 600, height: 800 },

  { key: '6x8-2split', label: '6x8 for 6x4 2 Splits', width: 600, height: 800 },
  { key: '6x8-3split', label: '6x8 for 6x4 3 Splits', width: 600, height: 800 },

  { key: '6x6', label: '6x6 / 152x152mm', width: 600, height: 600 },
  { key: '5x5', label: '5x5 / 127x127mm', width: 500, height: 500 },
  { key: '6x5', label: '6x5 / 152x127mm', width: 600, height: 500 },

  { key: '6x4-3split', label: '6x4 3 Split', width: 600, height: 400 }
];

const CARD_PRINTER_MODES = [
  { key: 'front', label: 'Front Only' },
  { key: 'front_silver', label: 'Front + Silver' },
  { key: 'duplex', label: 'Front / Back' },
  { key: 'duplex_silver_front', label: 'Front + Silver / Back' },
  { key: 'duplex_silver_both', label: 'Front + Silver / Back + Silver' }
];



const DEFAULT_PREFS = {
  isActive: true,
  orientation: 'Portrait',
  duplex: false,
  copies: 1,
  paperKind: '',
  printMode: '', // 🔥 NEW
  widthHundredths: '',
  heightHundredths: '',
};

function safeNumber(v, fallback = '') {
  if (v === null || v === undefined) return fallback;
  return String(v);
}

export default function PrinterDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();

  const printerId = useMemo(() => decodeURIComponent(pid || ''), [pid]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [printer, setPrinter] = useState(null);
  const [prefs, setPrefs] = useState({ ...DEFAULT_PREFS });

  const [selectedPaper, setSelectedPaper] = useState('custom');
  const [groups, setGroups] = useState([]);
  const [diagnostics, setDiagnostics] = useState(null);

  // -----------------------------
  // LOAD PRINTER
  // -----------------------------
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);

        const data = await getPrinterPrefs(printerId);
        if (!alive) return;

        setPrinter(data || null);

        const p = data || {};
        setPrefs({
          isActive: p.isActive ?? DEFAULT_PREFS.isActive,
          orientation: p.orientation || DEFAULT_PREFS.orientation,
          duplex: !!p.duplex,
          copies: Number.isFinite(Number(p.copies)) ? Number(p.copies) : DEFAULT_PREFS.copies,
          paperKind: p.paperKind || '',
          printMode: p.printMode || '',
          widthHundredths: safeNumber(p.widthHundredths, ''),
          heightHundredths: safeNumber(p.heightHundredths, ''),
        });

        setDirty(false);
      } catch {
        toast.error('Failed to load printer details');
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => (alive = false);
  }, [printerId]);

  useEffect(() => {
    if (prefs.printMode) {
      setSelectedPaper(prefs.printMode);
    } else {
      setSelectedPaper('custom');
    }
  }, [prefs.printMode]);

  // -----------------------------
  // GROUPS
  // -----------------------------
  useEffect(() => {
    fetchPrinterGroups().then(setGroups).catch(() => {
      toast.error('Failed to load printer groups');
    });
  }, []);

  // -----------------------------
  // DIAGNOSTICS
  // -----------------------------
  useEffect(() => {
    loadDiagnostics();
    const interval = setInterval(loadDiagnostics, 3000);
    return () => clearInterval(interval);
  }, [printerId]);

  async function loadDiagnostics() {
    try {
      const res = await apiFetch(`/api/printmanager/printers/${encodeURIComponent(printerId)}/diagnostics`);
      console.log("res:", res)
      setDiagnostics(res || null);
    } catch {}
  }

  function updatePref(key, value) {
    setPrefs((p) => ({ ...p, [key]: value }));
    setDirty(true);
  }

  // -----------------------------
  // PAPER OPTIONS (SMART)
  // -----------------------------
  const paperOptions = useMemo(() => {
    const id = printer?.printerId?.toLowerCase() || '';

    if (id.includes('joyspace')) {
      return PHOTO_PRINTER_PAPERS;
    }

    if (id.includes('dc760') || id.includes('rtai') || id.includes('card')) {
      return CARD_PRINTER_MODES;
    }

    return [];
  }, [printer]);

  // -----------------------------
  // SYNC SELECT
  // -----------------------------
  useEffect(() => {
    if (prefs.printMode) {
      setSelectedPaper(prefs.printMode);
      return;
    }

    if (prefs.paperKind) {
      setSelectedPaper(`std-${prefs.paperKind}`);
      return;
    }

    setSelectedPaper('custom');
  }, [prefs.printMode, prefs.paperKind]);

  // -----------------------------
  // HANDLE PAPER CHANGE
  // -----------------------------
  function handlePaperChange(e) {
    const v = e.target.value;

    if (v === 'custom') {
      setPrefs((p) => ({
        ...p,
        printMode: '',
        widthHundredths: '',
        heightHundredths: '',
      }));
      setDirty(true);
      return;
    }

    const selected = paperOptions.find(p => p.key === v);
    if (!selected) return;

    setPrefs((p) => ({
      ...p,
      printMode: selected.key,

      // only applies to photo printers
      widthHundredths: selected.width || '',
      heightHundredths: selected.height || '',
    }));

    setDirty(true);
  }

  async function savePrefs() {
    setSaving(true);
    try {
      await savePrinterPrefs(printerId, {
        ...prefs,
        printerGroupId: printer.printerGroupId || null,
      });
      toast.success('Saved');
      setDirty(false);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    setSending(true);
    try {
      await apiFetch("/api/printmanager/test-print", {
        method: "POST",
        body: {
          printerId: printer.printerId,
          deviceId: diagnostics?.deviceId,
        },
      });
      toast.success('Test sent');
    } catch {
      toast.error('Test failed');
    } finally {
      setSending(false);
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Loading…</div>;
  if (!printer) return <div style={{ padding: 20 }}>Printer not found</div>;

  return (
    <div className="printer-detail-page">
      <div className="printer-columns">

        {/* LEFT */}
        <div>
          <div className="card">
            <h2>Printer Detail</h2>
            <div><b>{printer.printerId}</b></div>

            <button className="btn-blue" onClick={() => navigate(-1)}>Return</button>
            <button className="btn-green" onClick={savePrefs} disabled={!dirty || saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

          <div className="card">
            <h3>Preferences</h3>

            <label>
              Active
              <input type="checkbox"
                checked={prefs.isActive}
                onChange={(e) => updatePref('isActive', e.target.checked)} />
            </label>

            <label>
              Orientation
              <select value={prefs.orientation}
                onChange={(e) => updatePref('orientation', e.target.value)}>
                <option>Portrait</option>
                <option>Landscape</option>
              </select>
            </label>

            <div className="setting-row">
              <label>
                Group&nbsp;
                <select
                  value={printer.printerGroupId || ''}
                  onChange={(e) => {
                    const newGroupId = e.target.value || null;
                    setPrinter(p => ({ ...p, printerGroupId: newGroupId }));
                    setDirty(true);
                  }}
                >
                  <option value="">— None —</option>
                  {groups.map(g => (
                    <option key={g._id} value={g._id}>{g.name}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="setting-row">
              <label>
                Paper&nbsp;
                <select value={selectedPaper} onChange={handlePaperChange}>
                  <option value="custom">(custom)</option>

                  {paperOptions.map(p => (
                    <option key={p.key} value={p.key}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="setting-row">
              <label>
                Width&nbsp;
                <input value={prefs.widthHundredths}
                  onChange={(e) => updatePref('widthHundredths', e.target.value)} />
              </label>
            </div>

            <div className="setting-row">
              <label>
                Height&nbsp;
                <input value={prefs.heightHundredths}
                  onChange={(e) => updatePref('heightHundredths', e.target.value)} />
              </label>
            </div>
          </div>
        </div>

        {/* MIDDLE */}
        <div>
          <div className="card">
            <h3>Test Print</h3>
            <button className="btn-green" onClick={sendTest} disabled={sending}>
              {sending ? 'Sending…' : 'Send Test'}
            </button>
          </div>

          <div className="card">
            <h3>Last Job</h3>
            <div><b>ID:</b> {diagnostics?.jobId || '—'}</div>
            <div><b>Status:</b> {diagnostics?.status || 'idle'}</div>
            <div><b>Updated:</b> {diagnostics?.updatedAt || '—'}</div>
          </div>
        </div>

        {/* RIGHT */}
        <div>
          <div className="card">
            <h3>Printer Status</h3>
            <div><b>State:</b> {diagnostics?.isOnline ? 'ONLINE' : 'OFFLINE'}</div>
            <div>Queue: {diagnostics?.queueDepth ?? '—'}</div>
          </div>
        </div>

      </div>
    </div>
  );
}
